/**
 * AuthGate — Session management for Nexum operators and developers.
 *
 * Three access levels:
 * - visitor: no auth, public pages only
 * - developer: API key (nxm_...), programmatic access
 * - operator: JWT via email/password login, full control plane
 *
 * Operators log in with email + password → backend returns JWT.
 * JWT stored in localStorage. PersonaBadge shows identity + role.
 */

import { useState, useEffect, createContext, useContext } from 'react';
import { getAdminToken, setAdminToken, clearAdminToken, setRefreshToken, refreshAccessToken } from '../services/platform-api';

export type AccessLevel = 'visitor' | 'developer' | 'operator';

const API_KEY_STORAGE = 'nexum_api_key';
const USER_STORAGE = 'nexum_user';

export interface UserSession {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface AuthState {
  accessLevel: AccessLevel;
  isAuthenticated: boolean;
  hydrating: boolean;
  apiKey: string | null;
  user: UserSession | null;
  loginApiKey: (key: string) => void;
  loginJwt: (token: string, user?: UserSession, refreshToken?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  accessLevel: 'visitor',
  isAuthenticated: false,
  hydrating: true,
  apiKey: null,
  user: null,
  loginApiKey: () => {},
  loginJwt: () => {},
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

function deriveAccessLevel(): AccessLevel {
  if (getAdminToken()) return 'operator';
  if (localStorage.getItem(API_KEY_STORAGE)) return 'developer';
  return 'visitor';
}

function loadUser(): UserSession | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(deriveAccessLevel);
  const [user, setUser] = useState<UserSession | null>(loadUser);
  const [hydrating, setHydrating] = useState(true);

  const loginApiKey = (key: string) => {
    localStorage.setItem(API_KEY_STORAGE, key);
    setAccessLevel('developer');
  };

  const loginJwt = (token: string, userInfo?: UserSession, refreshToken?: string) => {
    setAdminToken(token);
    if (refreshToken) setRefreshToken(refreshToken);
    if (userInfo) {
      localStorage.setItem(USER_STORAGE, JSON.stringify(userInfo));
      setUser(userInfo);
    }
    setAccessLevel('operator');
  };

  const logout = () => {
    clearAdminToken();
    localStorage.removeItem(API_KEY_STORAGE);
    localStorage.removeItem(USER_STORAGE);
    setAccessLevel('visitor');
    setUser(null);
  };

  // Hydration: validate stored token on mount. If expired, try refresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getAdminToken();
      if (!token) { setHydrating(false); return; }

      // Quick decode to check expiry (no verification — that's the backend's job)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = (payload.exp || 0) * 1000;
        const now = Date.now();

        if (expiresAt > now + 30_000) {
          // Token still valid (>30s remaining) — no action needed
          if (!cancelled) setHydrating(false);
          return;
        }

        // Token expired or about to expire — try refresh
        const ok = await refreshAccessToken();
        if (!cancelled) {
          if (ok) {
            setAccessLevel('operator');
          } else {
            clearAdminToken();
            localStorage.removeItem(USER_STORAGE);
            setAccessLevel('visitor');
            setUser(null);
          }
          setHydrating(false);
        }
      } catch {
        // Malformed token
        if (!cancelled) {
          clearAdminToken();
          localStorage.removeItem(USER_STORAGE);
          setAccessLevel('visitor');
          setUser(null);
          setHydrating(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-refresh timer: refresh access token before it expires (every 12 minutes for 15-min tokens)
  useEffect(() => {
    if (accessLevel !== 'operator') return;

    const REFRESH_INTERVAL = 12 * 60 * 1000; // 12 minutes
    const timer = setInterval(async () => {
      const ok = await refreshAccessToken();
      if (!ok) {
        clearAdminToken();
        localStorage.removeItem(USER_STORAGE);
        setAccessLevel('visitor');
        setUser(null);
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(timer);
  }, [accessLevel]);

  // Cross-tab sync
  useEffect(() => {
    const check = () => {
      setAccessLevel(deriveAccessLevel());
      setUser(loadUser());
    };
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  const isAuthenticated = accessLevel !== 'visitor';
  const apiKey = localStorage.getItem(API_KEY_STORAGE);

  return (
    <AuthContext.Provider value={{ accessLevel, isAuthenticated, hydrating, apiKey, user, loginApiKey, loginJwt, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * LoginPanel — Email/password login for operators.
 * Falls back to raw JWT paste if login endpoint fails.
 */
export function LoginPanel({ onLogin }: { onLogin?: () => void }) {
  const { loginJwt, accessLevel } = useAuth();
  const [mode, setMode] = useState<'login' | 'jwt'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [jwt, setJwt] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (accessLevel === 'operator') return null;

  const API_URL = import.meta.env.VITE_API_URL || 'https://api-bay-five-33.vercel.app';

  const handleLogin = async () => {
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Login failed');
        return;
      }
      loginJwt(data.access_token, data.user, data.refresh_token);
      onLogin?.();
    } catch {
      setError('Unable to reach the server. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleJwtSubmit = () => {
    const trimmed = jwt.trim();
    if (!trimmed || trimmed.split('.').length !== 3) {
      setError('Invalid JWT format');
      return;
    }
    loginJwt(trimmed);
    onLogin?.();
  };

  return (
    <div className="nx-auth-panel">
      <div className="nx-auth-panel__header">
        <h2>Operator Login</h2>
        <p className="nx-text-dim nx-text-xs">
          Sign in to access the Control Tower and govern capabilities.
        </p>
      </div>

      {mode === 'login' ? (
        <div className="nx-auth-panel__form">
          <input
            type="email"
            className="nx-input"
            placeholder="Email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            autoFocus
          />
          <input
            type="password"
            className="nx-input"
            placeholder="Password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />
          <button className="nx-btn nx-btn--primary" onClick={handleLogin} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>
      ) : (
        <div className="nx-auth-panel__form">
          <input
            type="password"
            className="nx-input nx-input--lg"
            placeholder="Paste admin JWT token..."
            value={jwt}
            onChange={(e) => { setJwt(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleJwtSubmit()}
            autoFocus
          />
          <button className="nx-btn nx-btn--primary" onClick={handleJwtSubmit}>
            Authenticate
          </button>
        </div>
      )}

      {error && <div className="nx-text-red nx-text-xs" style={{ marginTop: 4 }}>{error}</div>}

      <button
        className="nx-text-dim nx-text-xs"
        style={{ marginTop: 8, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
        onClick={() => { setMode(mode === 'login' ? 'jwt' : 'login'); setError(''); }}
      >
        {mode === 'login' ? 'Use JWT token instead' : 'Use email/password'}
      </button>
    </div>
  );
}

/**
 * PersonaBadge — Shows identity, role, and logout in navigation.
 */
export function PersonaBadge() {
  const { accessLevel, user, logout } = useAuth();

  if (accessLevel === 'visitor') return null;

  const label = accessLevel === 'operator'
    ? (user?.email ? `${user.role || 'operator'} · ${user.email}` : 'Operator')
    : 'Developer';
  const cls = accessLevel === 'operator' ? 'nx-persona-badge--operator' : 'nx-persona-badge--developer';

  return (
    <div className={`nx-persona-badge ${cls}`}>
      <span className="nx-status-dot" />
      <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <button className="nx-persona-badge__logout" onClick={logout} title="Sign out">
        &times;
      </button>
    </div>
  );
}

/**
 * RequireAuth — Wrapper that shows LoginPanel if not operator.
 * Waits for hydration to complete before deciding, preventing
 * the "flash of login screen" on page refresh with valid session.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { accessLevel, hydrating } = useAuth();

  if (hydrating) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, color: 'var(--nx-text-dim, #888)' }}>
        Verifying session...
      </div>
    );
  }

  if (accessLevel !== 'operator') {
    return (
      <div className="nx-require-auth">
        <LoginPanel />
      </div>
    );
  }

  return <>{children}</>;
}
